import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { app, isTeacher } from '../utils/firebase';
import {
  ClassAccessContext,
  disconnectKiosk,
  getStoredKioskSession,
  restoreKioskSession
} from '../utils/kioskAuth';

interface UseClassKioskAccessResult {
  access: ClassAccessContext | null;
  isKioskMode: boolean;
  authReady: boolean;
  authError: string | null;
  disconnect: () => Promise<void>;
}

export const useClassKioskAccess = (): UseClassKioskAccessResult => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [access, setAccess] = useState<ClassAccessContext | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const resolveAccess = useCallback(async () => {
    setAuthError(null);

    if (currentUser && isTeacher(currentUser)) {
      const { schoolId, homeroomClass } = currentUser;
      if (!schoolId || !homeroomClass?.grade || !homeroomClass?.class) {
        setAccess(null);
        setAuthReady(true);
        return;
      }

      setAccess({
        mode: 'teacher',
        schoolId,
        classId: `${schoolId}_${homeroomClass.grade}_${homeroomClass.class}`,
        grade: homeroomClass.grade,
        classNumber: homeroomClass.class,
        actorUid: currentUser.uid
      });
      setAuthReady(true);
      return;
    }

    const stored = getStoredKioskSession();
    if (!stored) {
      setAccess(null);
      setAuthReady(true);
      return;
    }

    try {
      const session = await restoreKioskSession();
      if (!session) {
        setAccess(null);
        setAuthReady(true);
        return;
      }

      const auth = getAuth(app);
      setAccess({
        mode: 'kiosk',
        schoolId: session.schoolId,
        classId: session.classId,
        grade: session.grade,
        classNumber: session.classNumber,
        actorUid: auth.currentUser?.uid ?? 'kiosk'
      });
    } catch (err) {
      console.error('키오스크 접근 복원 실패:', err);
      setAuthError('태블릿 연동이 만료되었습니다. 다시 연동해 주세요.');
      setAccess(null);
    } finally {
      setAuthReady(true);
    }
  }, [currentUser]);

  useEffect(() => {
    setAuthReady(false);
    void resolveAccess();
  }, [resolveAccess]);

  useEffect(() => {
    if (!getStoredKioskSession()) return;

    const intervalId = window.setInterval(() => {
      void restoreKioskSession().catch((err) => {
        console.error('키오스크 토큰 갱신 실패:', err);
      });
    }, 50 * 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  const disconnect = useCallback(async () => {
    await disconnectKiosk();
    setAccess(null);
    navigate('/class/kiosk/connect', { replace: true });
  }, [navigate]);

  return {
    access,
    isKioskMode: access?.mode === 'kiosk',
    authReady,
    authError,
    disconnect
  };
};
